"use client";

import { useParams, usePathname } from "next/navigation";
import { useEffect } from "react";
import VersionCheckProvider from "@/components/providers/version-check-provider";
import { AuthenticatedGuard } from "@/guards/authenticated";
import { ControlledProcessGuard } from "@/guards/controlled-process";
import { MeGuard } from "@/guards/me";
import {
  getPendingAbandonments,
  removePendingAbandonment,
  sendAbandonmentNormal,
} from "@/hooks/use-payment-intent-abandonment";
import useTabEnvironment from "@/hooks/use-tab-environment";
import { useUtmPersistence } from "@/hooks/use-utm-persistence";
import { initializeAuthSync } from "@/utils/auth-cookies";
import { MaintenanceBanner } from "./_components/maintenance-banner/maintenance-banner";
import { shouldUseMinimalLayout } from "./_utils/route-utils";

export default function RootLayoutClient({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? "";
  const params = useParams();
  const useMinimalLayout = shouldUseMinimalLayout(
    pathname,
    String(params.lng),
    Boolean(params.processUrl)
  );

  // Initialize auth sync on component mount
  useEffect(() => {
    initializeAuthSync();
  }, []);

  // Retry abandonments pendientes de sesiones offline o tab-close fallido
  useEffect(() => {
    const retryPending = async () => {
      const pending = getPendingAbandonments();
      for (const entry of pending) {
        const ok = await sendAbandonmentNormal(entry.piId, entry.reason);
        if (ok) {
          removePendingAbandonment(entry.piId);
        }
      }
    };

    void retryPending();
    window.addEventListener("online", retryPending);
    return () => window.removeEventListener("online", retryPending);
  }, []);

  // Restore tab-specific environment data from sessionStorage
  useTabEnvironment();
  // Persist UTM params once globally to keep attribution across internal navigation.
  useUtmPersistence();

  return (
    <AuthenticatedGuard>
      <ControlledProcessGuard>
        <MeGuard>
          <VersionCheckProvider />
          <div className="w-full scrollbar-stable">
            {!useMinimalLayout && <MaintenanceBanner />}
            {children}
          </div>
        </MeGuard>
      </ControlledProcessGuard>
    </AuthenticatedGuard>
  );
}
